// src/components/MissionForm.jsx
import React, { useMemo, useState,useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { collection, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { GTA_GAME_TABS as GAMES } from "../Community/components/ToolbarHeader";

const CATEGORIES = ["Main Story", "Side Mission", "Custom Mission"];
const DIFFICULTIES = ["Easy", "Normal", "Hard"];
const DEFAULT_MAP_IMG = "/assets/gta-map.jpg";
const SAMPLE_MISSION = {
  missionName: "Blitz Play",
  category: "Main Story",
  missionNumber: "38",
  chapter: "Heist: Blitz Play",
  location: "Downtown Los Santos – Del Perro Freeway",
  difficulty: "Hard",
  estDurationMins: "20",
  game: "GTA VI",
  gameKey: "gta-6",
  rewardsCash: "200000",
  rewardsItems: "Split among crew, unlocks further heists",
  tagsCsv: "heist, armored-truck, ambush, crew",

  coverImage:
    "https://images.unsplash.com/photo-1502877338535-766e1452684a?q=80&w=1600",
  gallery: [
    "https://images.unsplash.com/photo-1549924231-f129b911e442?q=80&w=1600",
    "https://images.unsplash.com/photo-1520697222862-2565f7b1f3d0?q=80&w=1600",
  ],
  videoUrl: "https://www.youtube.com/watch?v=qer6XxDq-YE",

  walkthrough: `### Step-by-step
1. Ram the garbage truck into the armored van at the designated intersection.
2. Use the sticky bombs to blow open the rear doors.
3. Collect the bonds from inside the truck.
4. Fight off waves of NOOSE and LSPD units.
5. Escape in getaway cars and lose the wanted level.
6. Regroup with the crew at the safe location.`,

  tips: `- Equip body armor before the mission begins.
- Use Michael's special ability (bullet time) to handle incoming SWAT teams.
- Park the getaway cars in alleys nearby for quick access.
- Franklin’s driving skill helps shake the cops.`,

  approaches: `- **Standard Ambush:** Follow the plan, block the truck with garbage vehicle, detonate, and hold position.
- **Aggressive:** Engage police immediately with heavy explosives to clear waves faster (higher risk).
- **Defensive:** Stay behind cover at the alley walls and pick off enemies systematically.`,

  story: `The FIB forces Michael, Franklin, and Trevor into performing a risky armored car heist in downtown Los Santos. Using an improvised garbage truck blockade, the crew ambushes the transport and battles through overwhelming police response to secure the bonds.`,

  primaryObjectives: [
    "Block the armored truck",
    "Breach the rear doors",
    "Collect the bonds",
    "Fight off law enforcement",
    "Escape and lose the heat",
  ],
  optionalObjectives: [
    "Complete without losing any crew members",
    "Finish in under 10 minutes",
    "Avoid more than 50% accuracy loss",
  ],

  arcId: "fib-heists",
  shortNote: "Ambush the armored truck in downtown LS.",
  city: "LS",
  mapImage: "",
  xPct: "48.2",
  yPct: "42.7",
};

export default function MissionForm({
  user,
  enforceAdmin = false,
  isAdmin = false,
  onSaved,
  onCancel,
  initial = {},
  extraMeta = {},
  missionId = null,
  mode = "create", // "create" | "edit"
}) {
  const isAuthed = Boolean(user?.uid);
  const blockedByAdminGuard = enforceAdmin && (!isAuthed || !isAdmin);

    // put above the component or inside it
const normalizeInitial = (init = {}) => {
  const obj = init || {};
  const tagsCsv = Array.isArray(obj.tags) ? obj.tags.join(", ") : (obj.tagsCsv || "");

  return {
    // direct
    missionName: obj.missionName ?? "",
    game: obj.game ?? "GTA VI",
    gameKey: obj.gameKey ?? "gta-6",
    category: obj.category ?? "Main Story",
    missionNumber: obj.missionNumber ?? "",
    chapter: obj.chapter ?? "",
    location: obj.location ?? "",
    difficulty: obj.difficulty ?? "Normal",
    estDurationMins: obj.estDurationMins ?? "",
    rewardsCash: obj.rewards?.cash ?? obj.rewardsCash ?? "",
    rewardsItems: obj.rewards?.items ?? obj.rewardsItems ?? "",
    tagsCsv,
    coverImage: obj.coverImage ?? "",
    gallery: Array.isArray(obj.gallery) && obj.gallery.length ? obj.gallery : [""],
    videoUrl: obj.videoUrl ?? "",
    walkthrough: obj.walkthrough ?? "",
    tips: obj.tips ?? "",
    approaches: obj.approaches ?? "",
    story: obj.story ?? "",
    // objectives (map from nested Firestore shape)
    primaryObjectives:
      (obj.objectives?.primary?.length ? obj.objectives.primary : obj.primaryObjectives) ?? [""],
    optionalObjectives:
      (obj.objectives?.optional?.length ? obj.objectives.optional : obj.optionalObjectives) ?? [""],
    // arc/map
    arcId: obj.arcId ?? "",
    shortNote: obj.shortNote ?? "",
    city: obj.city ?? "",
    mapImage: obj.mapImage ?? "",
    xPct: obj.map?.xPct ?? obj.xPct ?? "",
    yPct: obj.map?.yPct ?? obj.yPct ?? "",
    // visibility
    isPrivate: typeof obj.isPrivate === "boolean" ? obj.isPrivate : true,
  };
};

  const [form, setForm] = useState({
    missionName: "",
    game: "GTA VI",
    gameKey: "gta-6",
    category: "Main Story",
    missionNumber: "",
    chapter: "",
    location: "",
    difficulty: "Normal",
    estDurationMins: "",
    rewardsCash: "",
    rewardsItems: "",
    tagsCsv: "",
    coverImage: "",
    gallery: [""],
    videoUrl: "",
    walkthrough: "",
    tips: "",
    approaches: "",
    story: "",
    primaryObjectives: [""],
    optionalObjectives: [""],
    arcId: "",
    shortNote: "",
    city: "",
    mapImage: "",
    xPct: "",
    yPct: "",
    isPrivate: true,
    ...normalizeInitial(initial),
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState({
    walkthrough: false,
    tips: false,
    approaches: false,
    story: false,
  });

  const updateField = (name, value) =>
    setForm((s) => ({ ...s, [name]: value }));

  const updateArrayField = (arrKey, idx, value) =>
    setForm((s) => {
      const arr = [...s[arrKey]];
      arr[idx] = value;
      return { ...s, [arrKey]: arr };
    });

  const addArrayRow = (arrKey, blank = "") =>
    setForm((s) => ({ ...s, [arrKey]: [...s[arrKey], blank] }));

  const removeArrayRow = (arrKey, idx) =>
    setForm((s) => {
      const arr = s[arrKey].filter((_, i) => i !== idx);
      return { ...s, [arrKey]: arr.length ? arr : [""] };
    });

  const togglePreview = (key) => setPreviews((p) => ({ ...p, [key]: !p[key] }));

  // ---------- validation ----------
  const issues = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (blockedByAdminGuard)
      errs.push("You do not have permission to add missions.");
    // Game validation
    const validKeys = new Set(GAMES.map((g) => g.key));
    if (!validKeys.has(form.gameKey)) {
      errs.push("Please pick a valid game.");
    }
    // If key is valid, ensure label matches (helps keep data clean)
    const found = GAMES.find((g) => g.key === form.gameKey);
    if (found && found.label !== form.game) {
      // Not fatal, but we’ll normalize on submit; no error necessary.
    }
    if (!form.missionName.trim()) errs.push("Mission title is required.");
    if (!CATEGORIES.includes(form.category))
      errs.push(
        "Category must be Main Story, Side Mission, or Custom Mission."
      );

    if (!form.arcId.trim()) errs.push("Story Arc ID is required (arcId).");

    const hasX = String(form.xPct).trim() !== "";
    const hasY = String(form.yPct).trim() !== "";
    if (hasX ^ hasY) errs.push("Provide both X% and Y% for the map pin.");
    if (
      hasX &&
      (Number.isNaN(Number(form.xPct)) ||
        Number(form.xPct) < 0 ||
        Number(form.xPct) > 100)
    ) {
      errs.push("Map X% must be a number between 0 and 100.");
    }
    if (
      hasY &&
      (Number.isNaN(Number(form.yPct)) ||
        Number(form.yPct) < 0 ||
        Number(form.yPct) > 100)
    ) {
      errs.push("Map Y% must be a number between 0 and 100.");
    }

    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try {
        new URL(v.trim());
        return true;
      } catch {
        return false;
      }
    };
    if (form.coverImage && !urlLike(form.coverImage))
      errs.push("Cover Image URL is invalid.");
    if (form.videoUrl && !urlLike(form.videoUrl))
      errs.push("Video URL is invalid.");
    if (form.mapImage && !urlLike(form.mapImage))
      errs.push("Map Image URL is invalid.");

    form.gallery.forEach((u, i) => {
      if (u.trim() && !urlLike(u))
        errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    const numFields = [
      ["missionNumber", form.missionNumber],
      ["estDurationMins", form.estDurationMins],
      ["rewardsCash", form.rewardsCash],
    ];
    numFields.forEach(([label, val]) => {
      if (String(val).trim() !== "" && Number.isNaN(Number(val))) {
        errs.push(`${label} must be a number.`);
      }
    });

    const primaryFilled = form.primaryObjectives.filter((t) => t.trim()).length;
    if (primaryFilled < 1) errs.push("Add at least 1 primary objective.");

    return errs;
  }, [form, isAuthed, blockedByAdminGuard]);

  const canSubmit = issues.length === 0 && !saving;
  const mapPreviewSrc = form.mapImage?.trim() || DEFAULT_MAP_IMG;
  const showPin =
    String(form.xPct).trim() !== "" && String(form.yPct).trim() !== "";

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError("");

    try {
      // Normalize game fields from tabs in case labels changed
      const chosen = GAMES.find((g) => g.key === form.gameKey);
      const normalizedGame = chosen ? chosen.label : form.game;
      const existingCreatedBy = initial?.createdBy ?? user.uid;
      const payload = {
        game: normalizedGame,
        gameKey: form.gameKey,
        missionName: form.missionName.trim(),
        category: form.category,
        missionNumber:
          form.missionNumber === "" ? null : Number(form.missionNumber),
        chapter: form.chapter.trim() || null,
        location: form.location.trim() || null,
        difficulty: form.difficulty || null,
        estDurationMins:
          form.estDurationMins === "" ? null : Number(form.estDurationMins),
        rewards: {
          cash: form.rewardsCash === "" ? null : Number(form.rewardsCash),
          items: form.rewardsItems.trim() || null,
        },
        tags: form.tagsCsv
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        coverImage: form.coverImage.trim() || null,
        gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
        videoUrl: form.videoUrl.trim() || null,
        walkthrough: form.walkthrough.trim(),
        tips: form.tips.trim(),
        approaches: form.approaches.trim(),
        story: form.story.trim(),
        objectives: {
          primary: form.primaryObjectives.map((t) => t.trim()).filter(Boolean),
          optional: form.optionalObjectives
            .map((t) => t.trim())
            .filter(Boolean),
        },
        arcId: form.arcId.trim(),
        shortNote: form.shortNote.trim() || null,
        city: form.city.trim() || null,
        mapImage: form.mapImage.trim() || null,
        map: showPin
          ? { xPct: Number(form.xPct), yPct: Number(form.yPct) }
          : null,
        createdBy: user.uid,
        fanCreated: !isAdmin,
        isPrivate: Boolean(form.isPrivate),
        updatedAt: serverTimestamp(),
        ...extraMeta, // e.g., status: "pending", source: "user"
      };

       if (missionId) {
        // Editing existing mission
        
        payload.createdBy = existingCreatedBy; 
        await updateDoc(doc(db, "missions", missionId), payload);
        onSaved?.(missionId);
      } else {
        // Creating new mission
        payload.createdBy = user.uid;
        payload.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, "missions"), payload);
        onSaved?.(ref.id);
      }
    } catch (err) {
      console.error(err);
      setError("Could not save mission. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const loadSampleMission = () => {
    setForm((s) => ({
      ...s,
      ...SAMPLE_MISSION,
      isPrivate: s.isPrivate, // keep current selection
    }));
  };

  // When user changes game via select, set both label and key
  const setGameByKey = (key) => {
    const item = GAMES.find((g) => g.key === key);
    if (!item) return;
    setForm((s) => ({ ...s, game: item.label, gameKey: item.key }));
  };


useEffect(() => {
  // when editing and a mission arrives from Firestore, merge it in
  if (initial && Object.keys(initial).length) {
    setForm((prev) => ({ ...prev, ...normalizeInitial(initial) }));
  }
}, [initial]);
  return (
    <>
      {error && <div className="mt6-banner mt6-banner--error">{error}</div>}

      {/* 
        Render a single generic validation banner when there are issues AND the user is NOT signed in.
        If the user IS signed in, we intentionally hide the validation banner/list (validation still runs,
        and will block submission via the disabled Save button).
      */}
      {issues.length > 0 && !isAuthed && (
        <div className="mt6-banner mt6-banner--error" role="alert">
          Validation failed. Please correct the form errors before saving.
        </div>
      )}

      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- Basics ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Basics</h4>
          {/* Game */}
          {/* Game (UI only shows the friendly select; gameKey stays in state) */}
          <div>
            <label className="mt6-label">Game</label>
            <select
              className="mt6-input"
              value={form.gameKey}
              onChange={(e) => setGameByKey(e.target.value)}
              disabled={blockedByAdminGuard || saving}
              aria-label="Select game"
              title="Select game"
            >
              {GAMES.filter((g) => g.key !== "all").map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <label className="mt6-label">Title</label>
          <input
            className="mt6-input"
            value={form.missionName}
            onChange={(e) => updateField("missionName", e.target.value)}
            placeholder="e.g., Undercover Assignment"
            required
            disabled={blockedByAdminGuard || saving}
          />

          {/* Row 1: Mission # + Chapter */}
          <div className="mt6-row-2">
            <div>
              <label className="mt6-label">Mission # (optional)</label>
              <input
                className="mt6-input"
                value={form.missionNumber}
                onChange={(e) => updateField("missionNumber", e.target.value)}
                placeholder="e.g., 12"
                disabled={blockedByAdminGuard || saving}
              />
            </div>

            <div>
              <label className="mt6-label">Chapter / Act (optional)</label>
              <input
                className="mt6-input"
                value={form.chapter}
                onChange={(e) => updateField("chapter", e.target.value)}
                placeholder="e.g., Act II"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          {/* Row 2: Category + Visibility */}
          <div className="mt6-row-2">
            {/* Category */}
            <div>
              <label className="mt6-label">Category</label>
              <div className="mt6-pills">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`mt6-pill ${
                      form.category === c ? "is-active" : ""
                    }`}
                    onClick={() => updateField("category", c)}
                    disabled={blockedByAdminGuard || saving}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="mt6-label">Visibility</label>
              <div className="mt6-pills mt6-vis-inline">
                {[
                  { label: "Public", value: false },
                  { label: "Private", value: true },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    className={`mt6-pill ${
                      form.isPrivate === opt.value ? "is-active" : ""
                    }`}
                    onClick={() => updateField("isPrivate", opt.value)}
                    aria-pressed={form.isPrivate === opt.value}
                    disabled={blockedByAdminGuard || saving}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="mt6-muted mt6-vis-status" aria-live="polite">
                {form.isPrivate
                  ? "Private: visible only to you."
                  : "Public: visible to everyone."}
              </div>
            </div>
          </div>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Location (optional)</label>
              <input
                className="mt6-input"
                value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
                placeholder="e.g., Vice City – Port Area"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Difficulty (optional)</label>
              <select
                className="mt6-input"
                value={form.difficulty}
                onChange={(e) => updateField("difficulty", e.target.value)}
                disabled={blockedByAdminGuard || saving}
              >
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mt6-label">Est. Duration (mins)</label>
              <input
                className="mt6-input"
                value={form.estDurationMins}
                onChange={(e) => updateField("estDurationMins", e.target.value)}
                placeholder="e.g., 15"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>
        </div>

        {/* ---- Objectives ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Objectives</h4>

          <label className="mt6-label">Primary Objectives</label>
          {form.primaryObjectives.map((t, i) => (
            <div className="mt6-row" key={`po-${i}`}>
              <input
                className="mt6-input"
                value={t}
                onChange={(e) =>
                  updateArrayField("primaryObjectives", i, e.target.value)
                }
                placeholder={`Objective ${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("primaryObjectives", i)}
                disabled={
                  blockedByAdminGuard ||
                  saving ||
                  form.primaryObjectives.length <= 1
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={() => addArrayRow("primaryObjectives")}
            disabled={blockedByAdminGuard || saving}
          >
            ＋ Add Objective
          </button>

          <label className="mt6-label">Optional Objectives</label>
          {form.optionalObjectives.map((t, i) => (
            <div className="mt6-row" key={`oo-${i}`}>
              <input
                className="mt6-input"
                value={t}
                onChange={(e) =>
                  updateArrayField("optionalObjectives", i, e.target.value)
                }
                placeholder={`Optional Objective ${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("optionalObjectives", i)}
                disabled={
                  blockedByAdminGuard ||
                  saving ||
                  form.optionalObjectives.length <= 1
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={() => addArrayRow("optionalObjectives")}
            disabled={blockedByAdminGuard || saving}
          >
            ＋ Add Optional
          </button>
        </div>

        {/* ---- Content ---- */}
        {["walkthrough", "tips", "approaches", "story"].map((key) => (
          <div className="mt6-section" key={key}>
            <h4 className="mt6-h4">
              {
                {
                  walkthrough: "Walkthrough",
                  tips: "Tips & Tricks",
                  approaches: "Different Approaches",
                  story: "Story Summary",
                }[key]
              }
            </h4>
            <div className="mt6-segment">
              <button
                type="button"
                className={`mt6-seg-btn ${!previews[key] ? "is-active" : ""}`}
                onClick={() => togglePreview(key)}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previews[key] ? "is-active" : ""}`}
                onClick={() => togglePreview(key)}
              >
                Preview
              </button>
            </div>
            {!previews[key] ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                value={form[key]}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={
                  {
                    walkthrough: "Step-by-step guide…",
                    tips: "Helpful hints, loadouts, vehicles…",
                    approaches: "Stealth, loud, alternate routes…",
                    story: "Spoiler-safe synopsis…",
                  }[key]
                }
                disabled={blockedByAdminGuard || saving}
              />
            ) : (
              <div className="mt6-preview">
                {form[key].trim() ? (
                  <ReactMarkdown>{form[key]}</ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* ---- Rewards / Media ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Rewards & Media</h4>
          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Cash (optional)</label>
              <input
                className="mt6-input"
                value={form.rewardsCash}
                onChange={(e) => updateField("rewardsCash", e.target.value)}
                placeholder="e.g., 25000"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Items (optional)</label>
              <input
                className="mt6-input"
                value={form.rewardsItems}
                onChange={(e) => updateField("rewardsItems", e.target.value)}
                placeholder="e.g., Rifle, Armor"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Tags (CSV)</label>
              <input
                className="mt6-input"
                value={form.tagsCsv}
                onChange={(e) => updateField("tagsCsv", e.target.value)}
                placeholder="heist, stealth, car-chase"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          <label className="mt6-label">Cover Image URL</label>
          <input
            className="mt6-input"
            value={form.coverImage}
            onChange={(e) => updateField("coverImage", e.target.value)}
            placeholder="https://…"
            disabled={blockedByAdminGuard || saving}
          />

          <label className="mt6-label">Gallery URLs</label>
          {form.gallery.map((u, i) => (
            <div className="mt6-row" key={`g-${i}`}>
              <input
                className="mt6-input"
                value={u}
                onChange={(e) => updateArrayField("gallery", i, e.target.value)}
                placeholder={`Image URL #${i + 1}`}
                disabled={blockedByAdminGuard || saving}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeArrayRow("gallery", i)}
                disabled={
                  blockedByAdminGuard || saving || form.gallery.length <= 1
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={() => addArrayRow("gallery")}
            disabled={blockedByAdminGuard || saving}
          >
            ＋ Add Image
          </button>

          <label className="mt6-label">Video URL (optional)</label>
          <input
            className="mt6-input"
            value={form.videoUrl}
            onChange={(e) => updateField("videoUrl", e.target.value)}
            placeholder="https://youtube.com/…"
            disabled={blockedByAdminGuard || saving}
          />
        </div>

        {/* ---- Backstory & Map Pin ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Backstory & Map Pin</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Story Arc ID</label>
              <input
                className="mt6-input"
                value={form.arcId}
                onChange={(e) => updateField("arcId", e.target.value)}
                placeholder="e.g., prologue, main-caper, north-heists"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">City / Region (optional)</label>
              <input
                className="mt6-input"
                value={form.city}
                onChange={(e) => updateField("city", e.target.value)}
                placeholder="e.g., VC, LS"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Map Image URL (optional)</label>
              <input
                className="mt6-input"
                value={form.mapImage}
                onChange={(e) => updateField("mapImage", e.target.value)}
                placeholder="https://… (used in previews/overrides)"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
          </div>

          <label className="mt6-label">Short Note (tooltip)</label>
          <input
            className="mt6-input"
            value={form.shortNote}
            onChange={(e) => updateField("shortNote", e.target.value)}
            placeholder="Meet contact at the docks."
            disabled={blockedByAdminGuard || saving}
          />

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Map X% (0–100)</label>
              <input
                type="number"
                className="mt6-input"
                value={form.xPct}
                onChange={(e) => updateField("xPct", e.target.value)}
                placeholder="e.g., 62.5"
                min={0}
                max={100}
                step="0.1"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div>
              <label className="mt6-label">Map Y% (0–100)</label>
              <input
                type="number"
                className="mt6-input"
                value={form.yPct}
                onChange={(e) => updateField("yPct", e.target.value)}
                placeholder="e.g., 37.4"
                min={0}
                max={100}
                step="0.1"
                disabled={blockedByAdminGuard || saving}
              />
            </div>
            <div className="mt6-maphelp">
              <small className="mt6-muted">
                Tip: Use your Backstory Map screen to read % coords or eyeball
                in this preview.
              </small>
            </div>
          </div>

          {/* Live Preview */}
          <div className="mt6-mapbox">
            <div className="mt6-mapimg-wrap">
              <img
                src={mapPreviewSrc}
                alt="Map preview"
                className="mt6-mapimg"
              />
              {showPin && (
                <div
                  className="mt6-mappin"
                  style={{
                    left: `${Number(form.xPct)}%`,
                    top: `${Number(form.yPct)}%`,
                  }}
                  title={form.shortNote || form.missionName || "Mission"}
                >
                  <span className="mt6-mappin-dot" />
                  <span className="mt6-mappin-pulse" />
                </div>
              )}
            </div>
            <div className="mt6-maplegend">
              <code>
                X {String(form.xPct || "—")}%, Y {String(form.yPct || "—")}%
              </code>
              {form.shortNote ? (
                <span className="mt6-legend-note"> • {form.shortNote}</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* ---- Actions ---- */}
        <div className="mt6-actions">
          <button
            type="submit"
            className="mt6-btn mt6-btn--primary"
            disabled={!canSubmit || saving}
          >
            {saving ? "Saving…" : "Save Mission"}
          </button>

          {onCancel && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );
}
